import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Banner, Button, EmptyState, Panel, RowStatus, formatDuration, formatInt, useNow } from '@orgus/ui';
import { useOrgus, useSessions } from '../../lib/store.ts';
import { liveRows } from './liveRoom.ts';
import { useNeedsYouState } from '../asks/needsYou.ts';
import { blockedSince } from '../sessions/groups.ts';
import type { LiveRow } from './liveRoom.ts';

/**
 * Agents the room lists on the Agents page, what needs you first; the timeline under it shows
 * every run. Listing all of them drew 1,267 cards (17,000 elements) at scale and froze the
 * page as it opened (21 Oct 2026). The wall shows 8.
 */
const ROOM_ROWS = 12;
const WALL_ROWS = 8;

/** A held snapshot preserves both the facts and elapsed timers while paused. */
export function LiveRoom({ wall = false }: { wall?: boolean }) {
  const sessions = useSessions();
  const machines = useOrgus((s) => s.machines);
  const projects = useOrgus((s) => s.projects);
  const tick = useNow();
  const navigate = useNavigate();
  const [paused, setPaused] = useState(() => {
    try { return localStorage.getItem('orgus.live.paused') === 'true'; } catch { return false; }
  });
  // A waiting agent's timer starts when it asked, from the same items the Inbox reads (QA-4).
  const needs = useNeedsYouState();
  const askedAt = useMemo(() => blockedSince([...needs.blocked, ...needs.asked, ...needs.open]), [needs]);
  // Read again when the sessions or asks change, not on every tick of the clock.
  const live = useMemo(() => liveRows(sessions, askedAt), [sessions, askedAt]);
  const snapshot = useRef<{ rows: LiveRow[]; now: number }>({ rows: live, now: tick });
  const [hidden, setHidden] = useState(document.hidden);
  useEffect(() => {
    const visibility = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', visibility);
    return () => document.removeEventListener('visibilitychange', visibility);
  }, []);
  useEffect(() => {
    try { localStorage.setItem('orgus.live.paused', String(paused)); } catch { /* Storage may be unavailable. */ }
  }, [paused]);
  useEffect(() => {
    if (!wall) return;
    const key = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLElement && event.target.closest('button, input, textarea, select, a')) return;
      if (event.code === 'Space') { event.preventDefault(); setPaused((p) => !p); }
      if (event.key === 'Escape') navigate('/agents');
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [wall, navigate]);
  if (!paused && (!hidden || wall)) snapshot.current = { rows: live, now: tick };
  const { rows, now } = snapshot.current;
  const waiting = rows.filter((r) => r.waiting);
  const visible = [...waiting, ...rows.filter((r) => !r.waiting)].slice(0, wall ? WALL_ROWS : ROOM_ROWS);
  return <section aria-label={wall ? 'Agent wall' : 'Live room'} className="flex flex-col gap-4">
    <div className="flex flex-wrap items-center gap-3">
      <span className="num text-fg-muted">Needs you {formatInt(waiting.length)} · Working {formatInt(rows.length - waiting.length)}</span>
      <Button variant="secondary" size="sm" className="ml-auto" onClick={() => setPaused((p) => !p)} aria-pressed={paused}>{paused ? 'Resume live' : 'Pause live'}</Button>
      <Button variant="secondary" size="sm" onClick={() => navigate(wall ? '/agents' : `/wall${window.location.search}`)}>{wall ? 'Exit wall' : 'Open wall'}</Button>
    </div>
    {paused && <Banner title="Live paused">Resume live to see the latest work.</Banner>}
    {waiting.length > 0 && <Banner title="Needs you">Open a session to answer {formatInt(waiting.length)} waiting agents.</Banner>}
    {!rows.length ? <EmptyState title="No agents working" description="Agents appear here when they start work." /> : <Panel flush>
      <ul className="flex flex-col gap-3 p-4">
        {visible.map((r) => <li key={r.key} data-live-agent={r.key} className="flex flex-col gap-2 rounded-card bg-surface-raised p-3">
          <div className="flex flex-wrap items-center gap-3">
            <RowStatus tone={r.waiting ? 'attention' : 'working'} icon={r.waiting ? 'needs-you' : 'idle'} label={r.waiting ? 'Needs you' : 'Working'} />
            <span className="min-w-0 flex-1 truncate font-medium">{r.title}</span>
            <span role="timer" className="num text-fg-muted">{formatDuration(Math.max(0, now - r.startedAt))}</span>
            <Button size="sm" onClick={() => navigate(r.runId ? `/agents?run=${encodeURIComponent(r.runId)}` : `/sessions?session=${encodeURIComponent(r.sessionId)}`)}>Open {r.runId ? 'run' : 'session'}</Button>
          </div>
          <p className="truncate text-fg-muted">{r.line}</p>
          <p className="text-caption text-fg-subtle">{projects[r.projectId ?? '']?.name ?? 'No project'} · {machines[r.machineId]?.name ?? 'Unknown machine'} · <span className="num">{r.toolCalls == null ? 'Tool calls not reported' : `${formatInt(r.toolCalls)} tool calls`}</span></p>
        </li>)}
      </ul>
    </Panel>}
    {rows.length > visible.length && (
      <p className="num text-fg-muted">
        {formatInt(rows.length - visible.length)} more agents working. {wall ? 'Open Agents & workflows to see every agent.' : 'The timeline below shows every run.'}
      </p>
    )}
  </section>;
}
